import { useState, useRef } from "react";
import { Form, message } from "antd";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import * as Sentry from "@sentry/react";
import { checkGiftCard, verifyGiftCardService, upsertGiftCardTransaction } from "../../../../services";
import { logGiftCardDeduction } from "../../../../utils/dataIntegrity";
import { isServerReachable } from "../../../../utils/serverReachability";
import messages from "../../../../errors/messages.en-IN.json";

/**
 * useGiftCard
 *
 * Encapsulates all gift-card related logic that was formerly inline
 * inside PointOfsaleCore (~lines 1149-1460).
 *
 * @param {Object} deps
 * @param {Object}   deps.cart                  – current cart object
 * @param {Function} deps.setCart               – cart setter
 * @param {Object}   deps.tillData              – parsed tillData from localStorage
 * @param {Object}   deps.selectedPaymentMethod – currently selected payment method
 * @param {number}   deps.precision             – price precision (decimal places)
 * @param {boolean}  deps.isExchange            – whether in exchange mode
 * @param {Function} deps.upsertPOSLog          – POS log writer
 * @param {Object}   deps.authHeaders           – OAuth headers (from getOAuthHeaders)
 * @param {Object}   deps.db                    – database adapter (Dexie / SQLite)
 * @param {Function} deps.addProduct            – addProduct function from parent
 * @param {Function} deps.requestPayment        – requestPayment function from parent
 */
const useGiftCard = ({
  cart,
  setCart,
  tillData,
  selectedPaymentMethod,
  precision,
  isExchange,
  upsertPOSLog,
  authHeaders,
  db,
  addProduct,
  requestPayment,
  giftCardFlag,
  setGiftCardFlag,
}) => {
  // ── state ────────────────────────────────────────────────────────────
  const [giftCardForm] = Form.useForm();
  const [giftCardItems, setGiftCardItems] = useState([]);
  const [giftCardType, setGiftCardType] = useState("giftCard");
  const [selectGiftCardItem, setSelectGiftCardItem] = useState({});
  const [validateGiftCard, setValidateGiftCard] = useState(false);
  const [giftCardData, setGiftCardData] = useState([]);
  const [giftCardBalance, setGiftCardBalance] = useState(0);
  const [validateGiftCardForm] = Form.useForm();
  const [realPrice, setRealPrice] = useState(0);
  const [firstInputValue, setFirstInputValue] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGiftCardFlag, setIsGiftCardFlag] = useState(false);
  const [isNewGiftCardFlag, setIsNewGiftCardFlag] = useState(false);
  const redeemInFlightRef = useRef(false);

  // ── handlers ─────────────────────────────────────────────────────────

  const findMatchedGiftCardData = async () => {
    const giftCardDataArr = await db.giftCardData.toArray();
    return giftCardDataArr.find((giftcard) => giftcard.mProductId === selectGiftCardItem.mProductId);
  };

  const generateRefId = () => uuidv4().replace(/-/g, "").toUpperCase();

  const verifyGiftCard = async (data, matchedGiftCardData, refId, setAuthTokens) => {
    const query = {
      query: `
      mutation {
        verifyGiftCard(giftCards: [{
          cwrGiftcardTypeId: "${matchedGiftCardData?.cwrGiftcardTypeId}"
          cardNo: ${data.number ? `"${data.number}"` : null}
          referenceNo: "${refId}"
          b2cCustomerId: "${cart.customer?.cwrCustomerId || null}"
          cardPin: ${data.cardPin ? `"${data.cardPin}"` : null}
          currentBalance: ${parseFloat(data.amount)}
        }]) {
          status
          message
          cardNo
          expiryDate
        }
      }`,
    };

    const response = await verifyGiftCardService(query);
    if (response.data.data.verifyGiftCard.status !== "200") {
      message.error(response.data.data.verifyGiftCard.message);
    }
    return response.data.data.verifyGiftCard;
  };

  const processVerifiedGiftCard = (data, matchedGiftCardData, refId, giftCardResponse) => {
    setValidateGiftCard(false);
    setGiftCardBalance("0.00");

    const futureDate = dayjs().add(matchedGiftCardData.validity, "days");
    const updatedItem = {
      ...selectGiftCardItem,
      isGiftCard: true,
      realPrice: parseFloat(data.amount),
      total: parseFloat(data.amount),
      nettotal: parseFloat(data.amount),
      salePrice: parseFloat(data.amount),
      discAmount: 0,
      taxRate: 0,
      taxAmount: 0,
      unitPrice: parseFloat(data.amount),
      giftCardType: matchedGiftCardData.type,
      cardNo: matchedGiftCardData.type === "PHY" ? data.number : giftCardResponse.cardNo,
      expiryGiftCard: futureDate.format("DD-MM-YYYY"),
      grosslist: parseFloat(data.amount),
      grossstd: selectGiftCardItem.sunitprice,
      grossunit: parseFloat(data.amount),
      linegross: selectGiftCardItem.nettotal,
      linenet: parseFloat(data.amount),
      linetax: 0,
      listPrice: parseFloat(data.amount),
      netList: parseFloat(data.amount),
      netunit: parseFloat(data.amount),
    };

    const cardDetails = [
      ...giftCardData,
      {
        number: updatedItem.cardNo,
        amount: parseFloat(data.amount),
        refId,
      },
    ];
    if (!isExchange) {
      setCart({ ...cart, giftCardData: cardDetails });
    }
    setGiftCardData(cardDetails);
    upsertPOSLog(cart, "GCI");
    addProduct(updatedItem, 1, false, 0, false);
  };

  const handleGiftCardError = (data, refId, matchedGiftCardData, response) => {
    Sentry.captureException(new Error("Verify gift card failed"), {
      extra: JSON.stringify({
        cwrGiftcardTypeId: matchedGiftCardData?.cwrGiftcardTypeId || null,
        cardNo: data.number ? `"${data.number}"` : null,
        referenceNo: refId,
        b2cCustomerId: cart.customer?.cwrCustomerId || null,
        cardPin: data.cardPin ? `"${data.cardPin}"` : null,
        currentBalance: data.amount || null,
        response,
      }),
    });
  };

  const handleGiftCardDetails = async (data) => {
    try {
      const setAuthTokens = authHeaders?.access_token || "";
      const returnFlag = cart.isReturn === true || cart.isReturn === "Y" ? data.amount <= Math.abs(cart.total) : true;

      if (!returnFlag || data.amount <= 0) {
        message.error(`The ${giftCardType} ${messages.errors.gift_card.amount_exceeds_return}`);
        return;
      }

      const matchedGiftCardData = await findMatchedGiftCardData();
      if (!matchedGiftCardData) return;

      const refId = generateRefId();
      const giftCardResponse = await verifyGiftCard(data, matchedGiftCardData, refId, setAuthTokens);

      if (giftCardResponse?.status === "200") {
        processVerifiedGiftCard(data, matchedGiftCardData, refId, giftCardResponse);
      } else {
        handleGiftCardError(data, refId, matchedGiftCardData, giftCardResponse);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const redeemGiftCard = async (data) => {
    if (redeemInFlightRef.current) return;
    redeemInFlightRef.current = true;
    try {
      setIsSubmitting(true);

      // BUG-1887: probe reachability up-front (bounded ~4s) instead of letting the
      // 60s axios timeout expire before the cashier learns the network is down.
      const reachable = navigator.onLine ? await isServerReachable(4000) : false;
      if (!reachable) {
        message.error(`${messages.errors.network.connection_failed}`);
        setIsSubmitting(false);
        return;
      }

      const uniqId = uuidv4().replace(/-/g, "").toUpperCase();
      let giftCardAmount = 0;
      let giftCardGroup = "";
      const setAuthTokens = authHeaders?.access_token ?? "";

      try {
        const query = {
          query: `
            query {
              checkGiftCardBalance(cardNo: "${data.number}", cardPin: ${data.pin ? `"${data.pin}"` : null}) {
                currentBalance
                cardGroup
                status
                message
              }
            }`,
        };

        const giftCardResponse = await checkGiftCard(query);

        const balanceData = giftCardResponse?.data?.data?.checkGiftCardBalance?.[0];
        if (balanceData?.status === "200") {
          giftCardAmount = balanceData.currentBalance ?? 0;
          giftCardGroup = giftCardResponse.data.data.checkGiftCardBalance[0].cardGroup;
        }
      } catch (err) {
        message.error(err?.message || "Failed to check gift card balance");
        setIsSubmitting(false);
        return;
      }
      if (selectedPaymentMethod.name.toLowerCase() !== giftCardGroup.toLowerCase()) {
        message.error(`${giftCardGroup} redemption is not allowed through ${selectedPaymentMethod.name}`);
        setIsSubmitting(false);
        return;
      }
      if (cart?.total - cart?.paid >= data?.amount && data?.amount > 0) {
        try {
          const data1 = {
            query: `
              mutation {
                upsertGiftCardTransaction(giftCardTransactions: [{
                  cardNo: "${data.number}"
                  type: "RD"
                  referenceNo: "${uniqId}"
                  amount: ${parseFloat(data.amount)?.toFixed(precision)}
                  cardPin: ${data.pin ? `"${data.pin}"` : null}
                  tillId: ${tillData?.tillAccess?.cwrTill?.cwrTillID ? `"${tillData?.tillAccess?.cwrTill?.cwrTillID}"` : null}
                  b2c_Customer_Id: ${cart.customer.cwrCustomerId ? `"${cart.customer.cwrCustomerId}"` : null}
                }]) {
                  status
                  message
                }
              }`,
          };
          const upsertGiftCardResp = await upsertGiftCardTransaction(data1);

          const transactionData = upsertGiftCardResp?.data?.data?.upsertGiftCardTransaction;
          if (transactionData?.status === "200") {
            // DI-28: Log gift card deduction BEFORE order confirmation for audit trail
            logGiftCardDeduction({
              cardNo: data.number,
              amount: parseFloat(data.amount),
              referenceId: uniqId,
              sOrderID: cart?.sOrderID || null,
              tillId: tillData?.tillAccess?.cwrTill?.cwrTillID || null,
            });

            setGiftCardFlag(false);
            giftCardForm.resetFields();
            setGiftCardBalance(0);

            const cardDetails = [...(giftCardData ?? [])];
            const giftCardIndex = cardDetails.findIndex((item) => item.number === data.number);

            if (giftCardIndex >= 0) {
              cardDetails[giftCardIndex] = {
                ...cardDetails[giftCardIndex],
                number: data.number,
                amount: Math.min(data.amount, giftCardAmount + (cardDetails[giftCardIndex]?.amount ?? 0)),
                redemptionId: uniqId,
                pin: data.pin ?? null,
              };
            } else {
              cardDetails.push({
                number: data.number,
                amount: Math.min(data.amount, giftCardAmount),
                redemptionId: uniqId,
                pin: data.pin ?? null,
              });
            }
            // const filteredGiftCardNumbers = cardDetails.filter((card) => card.amount > 0).map((card) => card.number);
            const activeGiftCards = cardDetails.filter((card) => card.amount > 0);
            const latestRedemptionCards = cardDetails.filter((card) => card.redemptionId === uniqId && card.amount > 0);
            const combinedNumbers = [...new Set([...activeGiftCards.map((card) => card.number), ...latestRedemptionCards.map((card) => card.number)])];
            const updatedCart = { ...cart, giftCardData: cardDetails, giftCardNumbers: combinedNumbers };
            if (!isExchange) {
              setCart(updatedCart);
            }
            setGiftCardData(cardDetails);
            requestPayment(selectedPaymentMethod, Math.min(data.amount, giftCardAmount), null, null, null, updatedCart);
            upsertPOSLog(cart, "GCR");
            setIsSubmitting(false);
          } else {
            setIsSubmitting(false);
            message.error(transactionData?.message ?? `${messages.errors.gift_card.transaction_failed}`);
            Sentry.captureException(new Error("Gift card transaction failed"), {
              extra: {
                cardNo: data.number,
                type: "RD",
                referenceNo: uniqId,
                amount: data.amount,
                cardPin: data.pin ?? null,
                response: transactionData,
              },
            });
          }
        } catch (err) {
          setIsSubmitting(false);
          Sentry.captureException(err);
          message.error(err?.message || "Gift card redemption failed");
        }
      } else if (data?.amount > cart?.total - cart?.paid) {
        message.warning(`${messages.errors.cart.overpayment_not_allowed}`);
        setIsSubmitting(false);
      } else {
        message.error(`${messages.errors.gift_card.amount_exceeds_balance}`);
        setIsSubmitting(false);
      }
    } finally {
      redeemInFlightRef.current = false;
    }
  };

  const checkGiftCardBalance = async (formData, setAuthTokens) => {
    const query = {
      query: `
      query {
        checkGiftCardBalance(
          cardNo: "${formData.number}",
          cardPin: ${formData.pin ? `"${formData.pin}"` : null}
        ) {
          currentBalance
          cardGroup
          status
          message
        }
      }`,
    };

    const response = await checkGiftCard(query);

    // BUG-1887: checkGiftCard() (services/api/giftCards.js) catches its own axios
    // errors and returns undefined on failure (e.g. offline/network error), so
    // `response` can be undefined here — guard instead of letting this throw the
    // raw "Cannot read properties of undefined (reading 'data')" TypeError.
    return response?.data?.data?.checkGiftCardBalance?.[0];
  };

  const handleGiftCard = async (data) => {
    try {
      const formData = giftCardForm.getFieldsValue(true);
      const setAuthTokens = authHeaders?.access_token || "";

      if (!formData.number) {
        message.error(`${messages.errors.gift_card.number_required}`);
        return;
      }

      // BUG-1887: probe reachability up-front (bounded ~4s) instead of letting the
      // 60s axios timeout expire before the cashier learns the network is down.
      const reachable = navigator.onLine ? await isServerReachable(4000) : false;
      if (!reachable) {
        message.error(`${messages.errors.network.connection_failed}`);
        return;
      }

      const giftCardResponse = await checkGiftCardBalance(formData, setAuthTokens);

      if (giftCardResponse?.status === "200") {
        setGiftCardBalance(giftCardResponse.currentBalance ?? 0);
      } else {
        message.error(giftCardResponse?.message || `${messages.errors.gift_card.balance_fetch_failed}`);
      }
    } catch (error) {
      message.error(error.message || `${messages.errors.gift_card.processing_error}`);
    }
  };

  // ── public API ───────────────────────────────────────────────────────
  return {
    // state
    giftCardForm,
    giftCardItems,
    setGiftCardItems,
    giftCardType,
    setGiftCardType,
    selectGiftCardItem,
    setSelectGiftCardItem,
    validateGiftCard,
    setValidateGiftCard,
    giftCardData,
    setGiftCardData,
    giftCardBalance,
    setGiftCardBalance,
    validateGiftCardForm,
    realPrice,
    setRealPrice,
    firstInputValue,
    setFirstInputValue,
    isSubmitting,
    setIsSubmitting,
    giftCardFlag,
    setGiftCardFlag,
    isGiftCardFlag,
    setIsGiftCardFlag,
    isNewGiftCardFlag,
    setIsNewGiftCardFlag,

    // handlers
    handleGiftCardDetails,
    findMatchedGiftCardData,
    generateRefId,
    verifyGiftCard,
    processVerifiedGiftCard,
    handleGiftCardError,
    redeemGiftCard,
    handleGiftCard,
    checkGiftCardBalance,
  };
};

export default useGiftCard;
